import React from 'react'
import styled, { css } from 'styled-components'
import { brand } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'
import { shadows } from 'sutra/tokens/shadows'
import { typography } from 'sutra/tokens/typography'

export interface CardProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title'
> {
  $tinted?: boolean
  /** Header content, rendered at the start of a header row. */
  title?: React.ReactNode
  /** Header content, rendered at the end of the header row. */
  extra?: React.ReactNode
  /** Lifts the card and deepens its shadow on hover, with a pointer cursor. */
  hoverable?: boolean
}

const StyledCard = styled.div<{ $tinted: boolean; $interactive: boolean }>`
  background: ${brand.white};
  border-radius: ${radii.md}px;
  border: 1px solid #e9eaeb;
  box-shadow: ${shadows.card};
  padding: 18px;
  transition:
    transform ${motion.duration.fast}ms ${motion.ease},
    box-shadow ${motion.duration.fast}ms ${motion.ease};

  ${({ $interactive }) =>
    $interactive &&
    css`
      cursor: pointer;
      &:hover {
        transform: translateY(-1px);
        box-shadow: ${shadows.md};
      }
    `}

  ${({ $tinted }) =>
    $tinted &&
    css`
      background: linear-gradient(
        135deg,
        ${brand.stillNight[50]} 0%,
        ${brand.white} 100%
      );
      border-color: rgba(40, 54, 79, 0.22);
      box-shadow: none;
    `}
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  color: ${brand.stillNight[800]};
  font-size: ${typography.text.md.fontSize}px;
  font-weight: ${typography.weights.semibold};
  line-height: ${typography.text.md.lineHeight}px;
`

const Extra = styled.div`
  font-size: ${typography.text.sm.fontSize}px;
  font-weight: ${typography.weights.regular};
`

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    { $tinted = false, title, extra, hoverable = false, children, ...rest },
    ref,
  ) => (
    <StyledCard
      ref={ref}
      $tinted={$tinted}
      $interactive={hoverable || !!rest.onClick}
      {...rest}
    >
      {(title || extra) && (
        <Header>
          <div>{title}</div>
          {extra && <Extra>{extra}</Extra>}
        </Header>
      )}
      {children}
    </StyledCard>
  ),
)

Card.displayName = 'Card'
